// File: src/components/pages/payments/CollectionForm.jsx
// Payment collection form with dynamic calculation logic

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { X, Calculator, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { studentAPI, instructorConfigAPI, paymentAPI } from '../../../services/api';
import '../../../styles/unified.css';

const CollectionForm = ({ onClose, onPaymentCreated }) => {
  const { getAccessTokenSilently } = useAuth0();
  
  // Form state
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [instructorConfig, setInstructorConfig] = useState(null);
  
  // Payment fields
  const [paymentOption, setPaymentOption] = useState('Introductory');
  const [selectedHours, setSelectedHours] = useState(6);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [manualAmount, setManualAmount] = useState('');
  const [manualClasses, setManualClasses] = useState('');
  const [notes, setNotes] = useState('');
  
  // Calculated values
  const [calculation, setCalculation] = useState({
    hourlyRate: 0,
    originalAmount: 0,
    discountType: null,
    discountAmount: 0,
    finalAmount: 0,
    numberOfClasses: 0
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load students and instructor config on mount
  useEffect(() => {
    loadData();
  }, []);

  // Recalculate when dependencies change
  useEffect(() => {
    if (selectedStudent && instructorConfig) {
      calculatePayment();
    }
  }, [selectedStudent, paymentOption, selectedHours, manualAmount, manualClasses, instructorConfig]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load active students
      const response = await studentAPI.getStudentsByStatus('active', getAccessTokenSilently);
      
      console.log('Student API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Is Array?:', Array.isArray(response));
      
      // Handle different response formats
      let activeStudents = [];
      if (Array.isArray(response)) {
        activeStudents = response;
      } else if (response && Array.isArray(response.students)) {
        activeStudents = response.students;
      } else if (response && response.data && Array.isArray(response.data)) {
        activeStudents = response.data;
      } else {
        console.warn('Unexpected response format:', response);
        activeStudents = [];
      }
      
      console.log('Active students loaded:', activeStudents.length);
      setStudents(activeStudents);
      
      // Load instructor configuration
      const config = await instructorConfigAPI.getConfiguration(getAccessTokenSilently);
      console.log('Loaded instructor config:', config);
      
      if (!config) {
        setError('No instructor configuration found. Please configure your settings first.');
        setLoading(false);
        return;
      }
      
      setInstructorConfig(config);
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load students and configuration');
      setLoading(false);
    }
  };

  const calculatePayment = () => {
    if (!selectedStudent || !instructorConfig) {
      console.log('Cannot calculate: missing student or config');
      return;
    }

    console.log('Calculating payment for:', selectedStudent);
    console.log('Instructor config:', instructorConfig);
    console.log('Payment option:', paymentOption);

    let hourlyRate = 0;
    let originalAmount = 0;
    let discountType = null;
    let discountAmount = 0;
    let finalAmount = 0;
    let numberOfClasses = 0;

    // Determine hourly rate based on student's booking type preference
    if (selectedStudent.preferredBookingType === 'Automatic') {
      hourlyRate = instructorConfig.hourlyRateAutomatic || 0;
    } else {
      hourlyRate = instructorConfig.hourlyRateManual || 0;
    }

    console.log('Hourly rate determined:', hourlyRate);

    switch (paymentOption) {
      case 'Introductory':
        numberOfClasses = 10;
        originalAmount = hourlyRate * 10;
        discountType = 'Percentage';
        const introDiscount = instructorConfig.discountIntroductory || 0;
        discountAmount = originalAmount * (introDiscount / 100);
        finalAmount = originalAmount - discountAmount;
        break;

      case '6Hrs Plus':
        numberOfClasses = selectedHours;
        originalAmount = hourlyRate * selectedHours;
        discountType = 'Per Hour';
        const hourlyDiscount = instructorConfig.discount6HoursPlus || 0;
        discountAmount = selectedHours * hourlyDiscount;
        finalAmount = originalAmount - discountAmount;
        break;

      case 'Daily':
        numberOfClasses = 2;
        originalAmount = hourlyRate * 2;
        discountType = null;
        discountAmount = 0;
        finalAmount = originalAmount;
        break;

      case 'Exam':
        numberOfClasses = 1;
        originalAmount = 100;
        discountType = null;
        discountAmount = 0;
        finalAmount = 100;
        break;

      case 'Others':
        numberOfClasses = parseInt(manualClasses) || 0;
        originalAmount = parseFloat(manualAmount) || 0;
        discountType = null;
        discountAmount = 0;
        finalAmount = originalAmount;
        break;

      default:
        break;
    }

    console.log('Calculation result:', {
      hourlyRate,
      originalAmount,
      discountAmount,
      finalAmount,
      numberOfClasses
    });

    setCalculation({
      hourlyRate,
      originalAmount,
      discountType,
      discountAmount,
      finalAmount,
      numberOfClasses
    });
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    const student = students.find(s => s.studentId === studentId);
    setSelectedStudent(student);
  };

  const handlePaymentOptionChange = (e) => {
    setPaymentOption(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }

    if (paymentOption === 'Others' && (!manualAmount || !manualClasses)) {
      setError('Please enter payment amount and number of classes for Others option');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const paymentData = {
        studentId: selectedStudent.studentId,
        instructorId: selectedStudent.instructorId,
        studentName: `${selectedStudent.firstName} ${selectedStudent.lastName}`,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentAmount: calculation.finalAmount,
        originalAmount: calculation.originalAmount,
        paymentMethod,
        paymentOption,
        discountType: calculation.discountType,
        discountAmount: calculation.discountAmount,
        numberOfClasses: calculation.numberOfClasses,
        hourlyRate: calculation.hourlyRate,
        notes: notes.trim() || null
      };

      await paymentAPI.createPayment(paymentData, getAccessTokenSilently);

      setSuccess(true);
      setTimeout(() => {
        onPaymentCreated();
      }, 1500);
    } catch (err) {
      console.error('Error creating payment:', err);
      setError('Failed to create payment record');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="booking-success-banner">
        <CheckCircle size={24} />
        <span>Payment recorded successfully!</span>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="card-header">
        <Calculator size={20} />
        <h3>New Payment Collection</h3>
        <button onClick={onClose} className="btn-icon" style={{ marginLeft: 'auto' }}>
          <X size={20} />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card-body">
        {error && (
          <div className="booking-error-banner">
            <AlertCircle size={24} />
            <span>{error}</span>
          </div>
        )}

        {!instructorConfig && !loading && !error && (
          <div className="message-warning">
            <AlertCircle size={24} />
            <span>
              No instructor configuration found. Please set up your hourly rates and discounts in the 
              "Manage Configurations" section before collecting payments.
            </span>
          </div>
        )}

        {/* Student Selection */}
        <div className="form-field">
          <label className="form-label">Choose Student *</label>
          <select
            value={selectedStudent?.studentId || ''}
            onChange={handleStudentChange}
            className="form-select"
            required
            disabled={!students || students.length === 0}
          >
            <option value="">
              {students && students.length === 0 
                ? '-- No Active Students Available --' 
                : '-- Select Student --'}
            </option>
            {Array.isArray(students) && students.map(student => (
              <option key={student.studentId} value={student.studentId}>
                {student.firstName} {student.lastName} - {student.email}
              </option>
            ))}
          </select>
          {students && students.length === 0 && (
            <p className="form-helper" style={{ color: '#f59e0b', marginTop: '8px' }}>
              No active students found. Please add students first.
            </p>
          )}
        </div>

        {/* Payment Option */}
        <div className="form-field">
          <label className="form-label">Payment Option *</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            {['Introductory', '6Hrs Plus', 'Daily', 'Exam', 'Others'].map(option => (
              <label key={option} className="form-checkbox">
                <input
                  type="radio"
                  name="paymentOption"
                  value={option}
                  checked={paymentOption === option}
                  onChange={handlePaymentOptionChange}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 6Hrs Plus - Hours Selection */}
        {paymentOption === '6Hrs Plus' && (
          <div className="form-field">
            <label className="form-label">Select Hours *</label>
            <select
              value={selectedHours}
              onChange={(e) => setSelectedHours(parseInt(e.target.value))}
              className="form-select"
            >
              <option value={6}>6 Hours</option>
              <option value={8}>8 Hours</option>
              <option value={10}>10 Hours</option>
            </select>
          </div>
        )}

        {/* Others - Manual Entry */}
        {paymentOption === 'Others' && (
          <>
            <div className="form-field">
              <label className="form-label">Payment Amount (£) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={manualAmount}
                onChange={(e) => setManualAmount(e.target.value)}
                className="form-input"
                placeholder="Enter amount"
                required
              />
            </div>
            <div className="form-field">
              <label className="form-label">Number of Classes *</label>
              <input
                type="number"
                min="1"
                value={manualClasses}
                onChange={(e) => setManualClasses(e.target.value)}
                className="form-input"
                placeholder="Enter number of classes"
                required
              />
            </div>
          </>
        )}

        {/* Payment Method */}
        <div className="form-field">
          <label className="form-label">Payment Method *</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="form-select"
            required
          >
            <option value="Cash">Cash</option>
            <option value="Card">Card</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Cheque">Cheque</option>
          </select>
        </div>

        {/* Notes */}
        <div className="form-field">
          <label className="form-label">Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="form-textarea"
            rows="3"
            placeholder="Add any additional notes..."
          />
        </div>

        {/* Calculation Summary */}
        {selectedStudent && calculation.finalAmount > 0 && (
          <div className="config-summary-card" style={{ marginTop: '24px' }}>
            <h4 style={{ marginBottom: '16px', color: '#2563eb' }}>Payment Calculation</h4>
            
            <div className="config-summary-item">
              <span className="config-summary-label">Hourly Rate:</span>
              <span className="config-summary-value">£{(calculation.hourlyRate || 0).toFixed(2)}</span>
            </div>
            
            <div className="config-summary-item">
              <span className="config-summary-label">Number of Classes:</span>
              <span className="config-summary-value">{calculation.numberOfClasses || 0}</span>
            </div>
            
            <div className="config-summary-item">
              <span className="config-summary-label">Original Amount:</span>
              <span className="config-summary-value">£{(calculation.originalAmount || 0).toFixed(2)}</span>
            </div>
            
            {calculation.discountAmount > 0 && (
              <>
                <div className="config-summary-item">
                  <span className="config-summary-label">Discount Type:</span>
                  <span className="config-summary-value">{calculation.discountType || 'N/A'}</span>
                </div>
                <div className="config-summary-item">
                  <span className="config-summary-label">Discount Amount:</span>
                  <span className="config-summary-value" style={{ color: '#10b981' }}>
                    -£{(calculation.discountAmount || 0).toFixed(2)}
                  </span>
                </div>
              </>
            )}
            
            <div className="config-summary-item" style={{ 
              borderTop: '2px solid #e5e7eb', 
              paddingTop: '12px',
              marginTop: '12px'
            }}>
              <span className="config-summary-label" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                Final Amount:
              </span>
              <span className="config-summary-value" style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                £{(calculation.finalAmount || 0).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading || !selectedStudent}>
            <Save size={18} />
            {loading ? 'Saving...' : 'Save Payment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CollectionForm;